import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGet, apiPatch, apiPost, apiPut } from "../../lib/api";

type Integration = {
  id: string;
  branch_id: string | null;
  name: string;
  channel: string;
  enabled: boolean;
  config: Record<string, unknown>;
};

type MeOut = {
  user_id: string;
  role: string;
  tenant_id: string;
  telegram_chat_id: string | null;
};

type TelegramBookingTemplateOut = {
  template: string;
  default_template: string;
  placeholders: string[];
};

function telegramMode(row: Integration | undefined): string {
  return String(row?.config?.mode ?? "").toLowerCase();
}

function isSharedTelegram(row: Integration | undefined): boolean {
  return telegramMode(row) === "shared_platform_bot";
}

function hasExplicitTelegramToken(row: Integration | undefined): boolean {
  const token = row?.config?.bot_token;
  return typeof token === "string" && token.trim().length > 0;
}

function telegramPreferenceRank(row: Integration): number {
  if (row.channel !== "telegram") return 99;
  const shared = isSharedTelegram(row);
  const explicit = hasExplicitTelegramToken(row);
  if (!shared && explicit) return 0;
  if (!shared) return 1;
  if (shared && explicit) return 2;
  return 3;
}

export function ManagerIntegrationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState("");
  const [result, setResult] = useState("");
  const [setupResult, setSetupResult] = useState("");

  // Telegram link
  const [chatIdInput, setChatIdInput] = useState("");
  const [botTokenInput, setBotTokenInput] = useState("");
  const [integrationNameInput, setIntegrationNameInput] = useState("Main Telegram Bot");

  // Test send
  const [testChatId, setTestChatId] = useState("");
  const [testMessage, setTestMessage] = useState("Test message from SalonOS");
  const [testResult, setTestResult] = useState("");

  const [bookingTemplate, setBookingTemplate] = useState("");
  const [bookingTemplateDefault, setBookingTemplateDefault] = useState("");
  const [bookingTemplateResult, setBookingTemplateResult] = useState("");

  const list = useQuery({
    queryKey: ["manager-integrations"],
    queryFn: () => apiGet<{ items: Integration[] }>("/v3/integrations"),
  });

  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => apiGet<MeOut>("/v3/auth/me"),
  });

  const saveChatId = useMutation({
    mutationFn: () => apiPatch("/v3/auth/profile", { telegram_chat_id: chatIdInput || null }),
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      setResult(t("manager.integrations.result.savedChatId", { chatId: (data as MeOut).telegram_chat_id ?? t("manager.integrations.cleared") }));
    },
    onError: (err) => setResult(String(err)),
  });

  const testSend = useMutation({
    mutationFn: () =>
      apiPost(`/v3/integrations/${selectedId}/telegram/test-send`, {
        chat_id: testChatId,
        message: testMessage,
      }),
    onSuccess: (data) => setTestResult(JSON.stringify(data, null, 2)),
    onError: (err) => setTestResult(String(err)),
  });

  const telegramIntegrations = (list.data?.items ?? [])
    .filter((i) => i.channel === "telegram")
    .slice()
    .sort((a, b) => telegramPreferenceRank(a) - telegramPreferenceRank(b));
  const primaryTelegram = telegramIntegrations[0];
  const selectedIsTelegram = telegramIntegrations.some((i) => i.id === selectedId);
  const selectedTelegramId = selectedIsTelegram ? selectedId : (primaryTelegram?.id ?? "");
  const primaryTelegramIsShared = isSharedTelegram(primaryTelegram);
  const primaryTelegramHasExplicitToken = hasExplicitTelegramToken(primaryTelegram);
  const webhookSyncBlockedForShared = primaryTelegramIsShared && !primaryTelegramHasExplicitToken;

  const saveTelegramIntegration = useMutation({
    mutationFn: async () => {
      const token = botTokenInput.trim();
      if (!token) throw new Error(t("manager.integrations.error.enterToken"));

      await apiPost("/v3/integrations/telegram/validate-token", { bot_token: token });

      const target = primaryTelegram;
      const payload = {
        branch_id: target?.branch_id ?? null,
        channel: "telegram",
        name: integrationNameInput.trim() || target?.name || t("manager.integrations.defaultBotName"),
        config: { ...(target?.config ?? {}), mode: "custom_bot", bot_token: token },
        enabled: true,
      };

      if (target) {
        return apiPut<Integration>(`/v3/integrations/${target.id}`, payload);
      }
      return apiPost<Integration>("/v3/integrations", payload);
    },
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ["manager-integrations"] });
      setSelectedId(data.id);
      setSetupResult(t("manager.integrations.result.telegramActive", { name: data.name, id: data.id }));
    },
    onError: (err) => setSetupResult(String(err)),
  });

  const syncWebhook = useMutation({
    mutationFn: () => {
      if (!selectedTelegramId) throw new Error(t("manager.integrations.error.selectTelegramFirst"));
      return apiPost(`/v3/integrations/${selectedTelegramId}/telegram/webhook/sync`, {});
    },
    onSuccess: (data) => setSetupResult(JSON.stringify(data, null, 2)),
    onError: (err) => setSetupResult(String(err)),
  });

  const bookingTemplateQuery = useQuery({
    queryKey: ["manager-telegram-booking-template", selectedId],
    queryFn: () =>
      apiGet<TelegramBookingTemplateOut>(`/v3/integrations/${selectedId}/telegram/templates/customer-booking-confirmation`),
    enabled: Boolean(selectedId) && selectedIsTelegram,
  });

  useEffect(() => {
    if (!bookingTemplateQuery.data) return;
    setBookingTemplate(bookingTemplateQuery.data.template || "");
    setBookingTemplateDefault(bookingTemplateQuery.data.default_template || "");
  }, [bookingTemplateQuery.data]);

  useEffect(() => {
    if (!selectedId && list.data?.items?.length) {
      setSelectedId(list.data.items[0].id);
    }
  }, [list.data?.items, selectedId]);

  useEffect(() => {
    if (!primaryTelegram) return;
    if (!integrationNameInput) {
      setIntegrationNameInput(primaryTelegram.name || t("manager.integrations.defaultBotName"));
    }
    const current = primaryTelegram.config?.bot_token;
    if (!botTokenInput && typeof current === "string" && current) {
      setBotTokenInput(current);
    }
  }, [primaryTelegram, integrationNameInput, botTokenInput]);

  const saveBookingTemplate = useMutation({
    mutationFn: () =>
      apiPatch<TelegramBookingTemplateOut>(`/v3/integrations/${selectedId}/telegram/templates/customer-booking-confirmation`, {
        template: bookingTemplate,
      }),
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ["manager-telegram-booking-template", selectedId] });
      setBookingTemplateResult(t("manager.integrations.result.savedTemplate"));
      setBookingTemplate(data.template || "");
      setBookingTemplateDefault(data.default_template || "");
    },
    onError: (err) => setBookingTemplateResult(String(err)),
  });

  const currentChatId = me.data?.telegram_chat_id;

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">{t("manager.integrations.title")}</h1>

      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.integrations.activateBot")}</h2>
        {primaryTelegramIsShared ? (
          <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
            Shared platform bot is already active for this tenant. You can keep using it and finish onboarding now. Paste a bot token below only when you want to upgrade to your own branded bot.
          </div>
        ) : null}
        <p className="text-sm text-slate-500 mt-1 mb-3">
          {t("manager.integrations.activateBotHelp")}
        </p>
        <div className="grid md:grid-cols-2 gap-2">
          <input
            className="input"
            placeholder={t("manager.integrations.placeholder.integrationName")}
            value={integrationNameInput}
            onChange={(e) => setIntegrationNameInput(e.target.value)}
          />
          <input
            className="input"
            placeholder={t("manager.integrations.placeholder.botToken")}
            value={botTokenInput}
            onChange={(e) => setBotTokenInput(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <button
            className="btn"
            onClick={() => saveTelegramIntegration.mutate()}
            disabled={!botTokenInput.trim() || saveTelegramIntegration.isPending}
          >
            {saveTelegramIntegration.isPending
              ? t("manager.integrations.saving")
              : primaryTelegramIsShared
                ? "Upgrade to Own Bot"
                : t("manager.integrations.action.saveBot")}
          </button>
          <button
            className="btn secondary"
            onClick={() => syncWebhook.mutate()}
            disabled={!selectedTelegramId || syncWebhook.isPending || webhookSyncBlockedForShared}
            title={webhookSyncBlockedForShared ? "Shared platform bot uses platform-managed routing. Upgrade to your own bot to sync a dedicated tenant webhook." : undefined}
          >
            {syncWebhook.isPending ? t("manager.integrations.syncing") : t("manager.integrations.action.syncWebhook")}
          </button>
        </div>
        {webhookSyncBlockedForShared ? (
          <p className="text-xs mt-2 text-amber-700">
            Webhook sync is disabled in shared platform bot mode. Upgrade to your own bot when you need dedicated inbound commands and webhook routing.
          </p>
        ) : null}
        {setupResult && <pre className="text-xs bg-slate-100 p-2 rounded mt-3 overflow-x-auto">{setupResult}</pre>}
      </section>

      {/* Connected channels */}
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.integrations.connectedChannels")}</h2>
        <div className="grid gap-2 mt-2">
          {(list.data?.items ?? []).map((row) => (
            <button
              key={row.id}
              className={`text-left rounded-lg border p-3 ${selectedId === row.id ? "border-teal-600 bg-teal-50" : "border-slate-200 bg-white"}`}
              onClick={() => setSelectedId(row.id)}
            >
              <p className="font-semibold">{row.name}</p>
              <p className="text-xs text-slate-500">
                {row.channel} | {row.enabled ? t("common.enabled") : t("common.disabled")}
              </p>
            </button>
          ))}
        </div>
      </section>

      {/* Link your Telegram */}
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.integrations.linkTelegram")}</h2>
        <p className="text-sm text-slate-500 mt-1 mb-3">
          {t("manager.integrations.linkTelegramHelp1")} (<code>/today</code>, <code>/appointments</code>).
          <br />
          {t("manager.integrations.linkTelegramHelp2")}{" "}
          <a className="text-teal-600 underline" href="https://t.me/userinfobot" target="_blank" rel="noreferrer">@userinfobot</a>
          {" "} {t("manager.integrations.linkTelegramHelp3")}
        </p>
        {currentChatId && (
          <p className="text-sm text-teal-700 font-medium mb-2">
            {t("manager.integrations.currentlyLinked")} <code>{currentChatId}</code>
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <input
            className="input w-full sm:flex-1"
            placeholder={t("manager.integrations.placeholder.chatId")}
            value={chatIdInput}
            onChange={(e) => setChatIdInput(e.target.value)}
          />
          <button
            className="btn"
            onClick={() => saveChatId.mutate()}
            disabled={saveChatId.isPending}
          >
            {saveChatId.isPending ? t("manager.integrations.saving") : t("common.save")}
          </button>
          {currentChatId && (
            <button
              className="btn secondary"
              onClick={() => {
                setChatIdInput("");
                saveChatId.mutate();
              }}
              disabled={saveChatId.isPending}
            >
              {t("manager.integrations.action.unlink")}
            </button>
          )}
        </div>
        {result && <p className="text-sm mt-2 text-teal-700">{result}</p>}
      </section>

      {/* Send test message */}
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.integrations.sendTestMessage")}</h2>
        <p className="text-sm text-slate-500 mt-1 mb-3">
          {t("manager.integrations.sendTestHelp")}
        </p>
        <div className="grid md:grid-cols-2 gap-2">
          <input
            className="input"
            placeholder={currentChatId ? t("manager.integrations.placeholder.yourChatId", { chatId: currentChatId }) : t("manager.integrations.placeholder.recipientChatId")}
            value={testChatId}
            onChange={(e) => setTestChatId(e.target.value)}
          />
          <input
            className="input"
            placeholder={t("manager.integrations.placeholder.messageText")}
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {currentChatId && !testChatId && (
            <button className="btn secondary" onClick={() => setTestChatId(currentChatId)}>
              {t("manager.integrations.action.useMyChatId")}
            </button>
          )}
          <button
            className="btn"
            disabled={!selectedId || !testChatId || !testMessage || testSend.isPending}
            onClick={() => testSend.mutate()}
          >
            {testSend.isPending ? t("manager.integrations.sending") : t("manager.integrations.action.sendTest")}
          </button>
        </div>
        {!selectedId && (
          <p className="text-xs text-amber-600 mt-1">{t("manager.integrations.selectChannelToSend")}</p>
        )}
        {telegramIntegrations.length === 0 && (
          <p className="text-xs text-slate-500 mt-1">{t("manager.integrations.noTelegramIntegrations")}</p>
        )}
        <pre className="text-xs bg-slate-100 p-2 rounded mt-3 overflow-x-auto">{testResult || t("manager.integrations.noResultYet")}</pre>
      </section>

      {/* Telegram booking template */}
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.integrations.bookingTemplateTitle")}</h2>
        <p className="text-sm text-slate-500 mt-1 mb-3">
          {t("manager.integrations.bookingTemplateHelp")}{" "}
          {(bookingTemplateQuery.data?.placeholders ?? ["customer_name", "service_name", "staff_name", "start_at"])
            .map((key) => `{{${key}}}`)
            .join(", ")}
        </p>
        {!selectedId && <p className="text-xs text-amber-600 mb-2">{t("manager.integrations.selectTelegramFirst")}</p>}
        {selectedId && !selectedIsTelegram && <p className="text-xs text-amber-600 mb-2">{t("manager.integrations.selectedNotTelegram")}</p>}
        <textarea
          className="input min-h-[180px]"
          value={bookingTemplate}
          onChange={(e) => setBookingTemplate(e.target.value)}
          placeholder={t("manager.integrations.placeholder.template")}
          disabled={!selectedId || !selectedIsTelegram || bookingTemplateQuery.isLoading}
        />
        <div className="flex flex-wrap gap-2 mt-2">
          <button
            className="btn"
            disabled={!selectedId || !selectedIsTelegram || !bookingTemplate.trim() || saveBookingTemplate.isPending}
            onClick={() => saveBookingTemplate.mutate()}
          >
            {saveBookingTemplate.isPending ? t("manager.integrations.saving") : t("manager.integrations.action.saveTemplate")}
          </button>
          <button
            className="btn secondary"
            disabled={!bookingTemplateDefault}
            onClick={() => setBookingTemplate(bookingTemplateDefault)}
          >
            {t("manager.integrations.action.resetDefault")}
          </button>
        </div>
        {bookingTemplateResult && <p className="text-sm mt-2 text-teal-700">{bookingTemplateResult}</p>}
      </section>
    </div>
  );
}
